import { createContext, useContext, useState, useEffect } from 'react';
import env from '../config/env';
import * as realAuth from '../api/real/authApi';
import { mockOrders } from '../data/mockOrders';

const AuthContext = createContext();

// ─── Mock implementation ──────────────────────────────────────────────────────

const DEMO_USER = {
  id: 'usr_001',
  email: 'demo@skinnthrive.com',
  name: 'Priya Sharma',
  phone: '9876543210',
  avatar: null,
  emailVerified: true,
};

const mockLogin = async (email, password) => {
  await new Promise((r) => setTimeout(r, 800));
  if (password.length < 6) throw new Error('Invalid email or password');
  const u =
    email === DEMO_USER.email
      ? DEMO_USER
      : { id: `usr_${Date.now()}`, email, name: email.split('@')[0], phone: '', avatar: null, emailVerified: true };
  localStorage.setItem('skinnthrive_token', `mock_jwt_${Date.now()}`);
  localStorage.setItem('skinnthrive_user', JSON.stringify(u));
  return u;
};

// Mock register — simulates OTP flow (returns { success, email } without setting user)
const mockRegister = async (name, email, password) => {
  await new Promise((r) => setTimeout(r, 800));
  if (password.length < 8) throw new Error('Password must be at least 8 characters');
  // Don't set user yet — they need to verify OTP first
  return { success: true, email, expiresInMinutes: 10 };
};

// Mock verifyEmail — accepts any 6-digit code (use 123456 in dev)
const mockVerifyEmail = async (email, otp) => {
  await new Promise((r) => setTimeout(r, 700));
  if (otp.length !== 6) throw new Error('Invalid OTP. Please check and try again.');
  const u = { id: `usr_${Date.now()}`, email, name: email.split('@')[0], phone: '', avatar: null, emailVerified: true };
  localStorage.setItem('skinnthrive_token', `mock_jwt_${Date.now()}`);
  localStorage.setItem('skinnthrive_user', JSON.stringify(u));
  return u;
};

const mockResendOtp = async (email) => {
  await new Promise((r) => setTimeout(r, 600));
  return { success: true, email, expiresInMinutes: 10 };
};

const mockLogout = () => {
  localStorage.removeItem('skinnthrive_token');
  localStorage.removeItem('skinnthrive_refresh_token');
  localStorage.removeItem('skinnthrive_user');
};

const mockGetOrders = (user) => {
  if (!user) return [];
  return user.email === DEMO_USER.email ? mockOrders : [];
};

// ─── Provider ─────────────────────────────────────────────────────────────────

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // OTP verification pending state — used to show OTP screen on register/login
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState(null);
  const [isEmailVerificationPending, setIsEmailVerificationPending] = useState(false);

  useEffect(() => {
    const token     = localStorage.getItem('skinnthrive_token');
    const savedUser = localStorage.getItem('skinnthrive_user');
    if (token && savedUser) {
      try { setUser(JSON.parse(savedUser)); } catch {}
    }
    setIsLoading(false);

    // Handle session expiry from axios interceptor — clears user without hard reload
    const onExpired = () => {
      setUser(null);
      // Redirect to login without page reload if on a protected path
      if (window.location.pathname.startsWith('/account')) {
        window.location.replace('/login');
      }
    };
    window.addEventListener('customer-session-expired', onExpired);
    return () => window.removeEventListener('customer-session-expired', onExpired);
  }, []);

  // Step 1 of registration — creates account + triggers OTP email
  // Returns { success, email, expiresInMinutes } (no JWT yet)
  const register = async (name, email, password) => {
    const result = env.isMock
      ? await mockRegister(name, email, password)
      : await realAuth.register(name, email, password);
    setPendingVerificationEmail(result.email);
    setIsEmailVerificationPending(true);
    return result;
  };

  // Step 2 of registration — verifies OTP, sets user + JWT
  const verifyEmail = async (email, otp) => {
    const u = env.isMock
      ? await mockVerifyEmail(email, otp)
      : await realAuth.verifyEmail(email, otp);
    setPendingVerificationEmail(null);
    setIsEmailVerificationPending(false);
    setUser(u);
    return u;
  };

  const resendOtp = async (email) => {
    return env.isMock
      ? await mockResendOtp(email)
      : await realAuth.resendOtp(email);
  };

  // Login — handles EMAIL_NOT_VERIFIED by setting pending state
  const login = async (email, password) => {
    try {
      const u = env.isMock
        ? await mockLogin(email, password)
        : await realAuth.login(email, password);
      setUser(u);
      return u;
    } catch (err) {
      if (err.errorCode === 'EMAIL_NOT_VERIFIED') {
        setPendingVerificationEmail(err.email || email);
        setIsEmailVerificationPending(true);
      }
      throw err;
    }
  };

  // Logout — clears auth but preserves cart
  const logout = async () => {
    if (env.isMock) {
      mockLogout();
    } else {
      await realAuth.logout();
    }
    setUser(null);
    // Note: cart is intentionally NOT cleared so guest can continue shopping
  };

  const getOrders = async () => {
    if (env.isMock) return mockGetOrders(user);
    return realAuth.getOrders();
  };

  const clearPendingVerification = () => {
    setPendingVerificationEmail(null);
    setIsEmailVerificationPending(false);
  };

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      isAuthenticated: !!user,
      pendingVerificationEmail,
      isEmailVerificationPending,
      login,
      register,
      verifyEmail,
      resendOtp,
      logout,
      getOrders,
      clearPendingVerification,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};
